import type { ListerCardCondition } from "./card-condition";
import type { ListerAutographType } from "./card-autograph";

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`${status}`);
  }
}

export type SavedSearch = {
  id: number;
  name: string;
  category: string;
  keyword: string;
  ebay_category_id: string | null;
  graders: string[] | null;
  grades: string[] | null;
  language: string[] | null;
  price_min_cents: number | null;
  price_max_cents: number | null;
  alert_threshold: number;
  alert_window_minutes: number;
  min_sample_size: number;
  webhook_url: string | null;
  sound_enabled: boolean;
  candidate_cap: number;
  per_search_daily_budget_cents: number | null;
  scan_ending_window_hours: number;
  required_title_substring: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
};

export type SavedSearchInput = Partial<Omit<SavedSearch, "id" | "created_at" | "updated_at">> & {
  name: string;
  category: string;
  keyword: string;
};

export type LiveListing = {
  id: number;
  saved_search_id: number;
  ebay_item_id: string;
  title: string;
  url: string;
  image_url: string | null;
  current_bid_cents: number;
  shipping_cents: number;
  total_cents: number;
  bid_count: number;
  watchers: number | null;
  end_time: string;
  buying_format: string;
  card_key_hash: string | null;
  resale_floor_cents: number | null;
  sold_floor_cents: number | null;
  bin_floor_cents: number | null;
  sold_median_cents: number | null;
  sold_high_cents: number | null;
  sold_sample: number;
  bin_sample: number;
  deal_score: number | null;
  market_direction: string;
  status: string;
  last_priced_at: string | null;
  last_seen_at: string;
  pricecharting_product_id?: string | null;
  tcgplayer_product_id?: string | null;
  card_set?: string | null;
  card_number?: string | null;
  card_rarity?: string | null;
  card_profile_resolved_at?: string | null;
};

export type SoldComp = {
  ebay_item_id: string;
  title: string;
  sold_price_cents: number;
  shipping_cents: number;
  total_cents: number;
  sold_date: string;
  link: string;
};

export type BinListing = {
  ebay_item_id: string;
  title: string;
  price_cents: number;
  shipping_cents: number;
  total_cents: number;
  link: string;
};

export type ListingComps = { sold: SoldComp[]; bin: BinListing[] };

export type TcgProduct = {
  product_id: string;
  url: string;
  name: string;
  set_name: string | null;
  rarity: string | null;
  card_number: string | null;
  market_price_cents: number | null;
  recent_sale_cents: number | null;
  low_listing_cents: number | null;
  listings_count: number | null;
  volatility: string | null;
};

export type PsaSpec = {
  spec_id: string;
  url: string;
  card_name: string;
  set_name: string | null;
  year: string | null;
  pop_total: number | null;
  pop_psa10: number | null;
  pop_psa9_5: number | null;
  pop_psa9: number | null;
  pop_psa8: number | null;
  pop_psa7: number | null;
};

export type PsaAuctionSale = {
  sold_at: string | null;
  price_cents: number | null;
  grade: string | null;
  source: string | null;
  lot_url: string | null;
};

export type PsaCert = {
  cert_no: string;
  card_name: string | null;
  set_name: string | null;
  year: string | null;
  grade: string | null;
  status: "ok" | "challenge" | "not_found" | "error";
};

export type ListingPsa = {
  spec: PsaSpec | null;
  sales: PsaAuctionSale[];
  cert: PsaCert | null;
};

export type PriceChartingProduct = {
  product_id: string;
  product_name: string;
  console_name: string | null;
  release_date: string | null;
  loose_price_cents: number | null;          // ungraded
  cib_price_cents: number | null;            // PSA 7/7.5
  new_price_cents: number | null;            // PSA 8/8.5
  graded_price_cents: number | null;         // PSA 9
  box_only_price_cents: number | null;       // PSA 9.5
  manual_only_price_cents: number | null;    // PSA 10
  bgs_10_price_cents: number | null;         // BGS 10
  condition_17_price_cents: number | null;   // CGC 10
  condition_18_price_cents: number | null;   // SGC 10
};

export type ScanRun = {
  id: number;
  saved_search_id: number;
  started_at: string;
  finished_at: string | null;
  live_count: number;
  repriced_count: number;
  serpapi_calls: number;
  browse_calls: number;
  sonnet_calls: number;
  cost_cents: number;
  status: string;
  notes: string | null;
};

export type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

export type SavedSearchSelector = number | "all";

export type HotDeal = {
  game: string;
  verdict: "STRONG_BUY" | "BUY" | "WATCH" | "SKIP" | "UNSCORABLE";
  edge_score: number;
  raw_gap_pct: number;
  gross_dollars: number;
  seller_trust: number;
  freshness: number;
  image_multiplier: number;
  liquidity: "hot" | "warm" | "cold" | "unknown";
  reason: string;
  card_name: string;
  card_number: string;
  card_set: string | null;
  is_graded: boolean;
  grader: "PSA" | "SGC" | "BGS" | "CGC" | null;
  grade: string | null;
  ebay_keyword: string;
  listing_id: string;
  listing_url: string;
  listing_title: string;
  listing_total: number;
  listing_ship: number;
  listing_image: string | null;
  seller_name: string | null;
  seller_fb: number | null;
  seller_pct: number | null;
  seller_joined_at: string | null;
  image_verdict: "KEEP" | "DEMOTE" | "UNCERTAIN" | null;
  image_detail: string | null;
  psa_cert: string | null;
  psa_spec_id: string | null;
  psa_set: string | null;
  psa_confirmed: boolean;
  sold_median: number;
  sold_source: "ebay-market-data" | "serpapi-sold";
  sold_count_7d: number;
  sold_count_30d: number;
  sold_last_at: string | null;
  last_sold: number | null;
  avg_sold: number | null;
  sold_low: number | null;
  sold_high: number | null;
  trend_dir: "up" | "down" | "flat" | null;
  trend_pct: number | null;
  net_after_fees: number | null;
  est_profit: number | null;
  active_bins: number;
  is_lowest_bin: boolean;
  pc_reference: number | null;
  pc_divergence_pct: number | null;
  pc_flag: boolean;
  sold_audit: {
    median: number;
    method: string;
    n_used: number;
    rejected: { junk: number; wrong_grade: number; outlier: number; foreign: number };
    comps: Array<{ item_id: string; title: string; total: number; sold_date: string; url: string; days_ago: number | null }>;
  };
};

export type HotDealsSnapshot = {
  refreshed_at: string;
  candidates: Array<{
    card_name: string;
    card_number: string;
    set_hint: string | null;
    grader: string | null;
    grade: string | null;
    sold_count: number;
    sold_median: number;
    ebay_keyword: string;
  }>;
  deals: HotDeal[];
  counts: { strong_buy: number; buy: number; watch: number; skip: number; unscorable: number };
  coverage?: {
    cards_checked: number;
    listings_examined: number;
    by_game: Record<string, number>;
    by_grade: Record<string, number>;
  };
  duration_seconds: number;
};

export type HotDealsStatus = {
  running: boolean;
  progress: { done: number; total: number } | null;
  refreshed_at: string | null;
  duration_seconds: number | null;
  counts: HotDealsSnapshot["counts"] | null;
};

export type ListerStatus = "scanned" | "researching" | "researched" | "identified" | "proposing" | "proposed" | "posting" | "posted" | "restocked" | "enriched" | "failed";
export type ListerStep = { at: string; step: string; ok: boolean; ms: number; note?: string };
export type ListerExisting = { id: string; title: string; handle: string; status: string; variant_id: string; inventory_item_id: string; price_cents: number; available: number | null; barcode: string | null; admin_url: string };
/** Mirrors `ProductDetail` (src/shopify/products.ts) exactly — everything an enrich needs to
 *  compare against, fetched live from the product Shopify actually holds right now. */
export type ListerProductDetail = {
  id: string; title: string; handle: string; status: string; admin_url: string;
  description_html: string; vendor: string; product_type: string; tags: string[];
  category: { id: string; full_name: string } | null;
  seo: { title: string; description: string };
  media: Array<{ id: string; url: string | null }>;
  metafields: Array<{ namespace: string; key: string; type: string; value: string }>;
  variant: { id: string; sku: string | null; barcode: string | null; price_cents: number; cost_cents: number | null; weight_lb: number | null; inventory_item_id: string; available: number | null };
};
export type ListerEnrichRow = { key: string; label: string; current: string; proposed: string; empty: boolean; changed: boolean; auto: boolean; tick_only: boolean };
export type ListerEvidence = { source: string; id: string; url?: string; title?: string; price_cents?: number; price_kind?: string; condition?: string; sold_at?: string; images: string[]; matched_by: "gtin" | "title" | "cert" | "card" };
export type ListerImage = { url: string; source: string; width: number | null; height: number | null; local_path: string | null; rank: number; served_url: string | null };
export type ListerResearch = { best_title: string | null; evidence: ListerEvidence[]; images: ListerImage[]; sources_ok: string[]; sources_failed: Array<{ source: string; reason: string }> };
export type ListerIdentity = { official_title: string; manufacturer: string | null; product_form: string | null; form_detail: string | null; language: string | null; msrp_cents: number | null; release_date: string | null; short_facts: string[]; confidence: number; conflicts: Array<{ topic: string; note: string; evidence_ids: string[] }>; shared_gtin_warning: boolean; field_support: Array<{ field: string; confidence: number; evidence_ids: string[] }> };
export type ListerRefute = { refuted: boolean; severity: "none" | "minor" | "major"; reasons: Array<{ claim: string; evidence_ids: string[] }>; alternative_identity: string | null };
export type ListerVerdict = { level: "green" | "yellow" | "red"; headline: string; points: string[] };
export type ListerAttributeValue = { taxonomy_value_id: string; name: string };
export type ListerSchemaAttribute = { attribute_id: string; name: string; kind: "choice" | "measurement"; metafield_key: string | null; metaobject_type: string | null; type_verified: boolean; values: ListerAttributeValue[]; options: Array<{ key: string; value: string }> };
export type ListerSchema = { category: { id: string; full_name: string; is_leaf: boolean }; attributes: ListerSchemaAttribute[]; unresolved: Array<{ attribute_id: string; name: string; reason: "no_template" | "unsupported_kind" | "entries_unavailable" }> };
export type ListerProposal = {
  category: { id: string; full_name: string; reason: string; alternatives: Array<{ id: string; full_name: string }> };
  product_type: string;
  vendor: string;
  title: string;
  description_html: string;
  seo: { title: string; description: string };
  tags: Array<{ value: string; is_new: boolean }>;
  collections: Array<{ handle: string | null; title: string; is_new: boolean; rule_tag: string }>;
  attributes: Array<{ key: string; name: string; taxonomy_value_ids: string[]; value_names: string[] }>;
  metafields: Array<{ namespace: string; key: string; value: string }>;
  weight_lb: number;
  quantity: number;
  barcode: string;
  sku: string | null;
  price: { suggested_cents: number; basis: string; msrp_cents: number | null; sold_median_cents: number | null; sold_n: number; active_low_cents: number | null; active_n: number; floor_cents: number | null };
  compare_at_cents: number | null;
  channels: { online: boolean; pos: boolean; shop: boolean; headless: boolean; facebook: boolean };
  status_after_post: "ACTIVE" | "DRAFT";
  notes_for_operator: string[];
};
export type ListerEdits = Partial<Omit<ListerProposal, "price">> & { price_cents?: number; cost_cents?: number | null; selected_images?: string[]; accepted_new_collections?: string[]; rejected_new_tags?: string[]; card_condition?: ListerCardCondition | null; card_autograph_type?: ListerAutographType | null };

/** Mirrors src/lister/types.ts's ListerMode. `item` (send-to-lister task L6): a lot, an "other"
 *  item, or a sealed item with no UPC, from Buy / Trade — no GTIN, no card, never researched. */
export type ListerMode = "barcode" | "card" | "item";
export type ListerCardRead = {
  is_slab: boolean; grader: string | null; grade: string | null; tier: string | null; cert: string | null;
  category: "tcg" | "sports" | "non_sports"; game: string | null; sport: string | null; player: string | null;
  name: string | null; name_original: string | null; set: string | null; set_code: string | null; number: string | null;
  year: string | null; manufacturer: string | null; parallel: string; serial: string | null;
  autograph: boolean; relic: boolean; language: string | null; rarity: string;
  confidence: "high" | "medium" | "low"; notes: string;
};
export type ListerCertRecord = { status: "ok" | "not_found" | "blocked" | "error"; grader: string; cert: string; name: string | null; set: string | null; number: string | null; grade: string | null; tier: string | null; year: string | null; population: string | null; raw_title: string | null; source: "api" | "web"; fetched_at: string };
/** The reconciled identity facts — the server has already applied "the grader's record wins"
 *  (spec §8), so the UI never re-derives precedence from `record` vs `read`. */
export type ListerCardFacts = {
  grader: string | null; grade: string | null; tier: string | null; cert: string | null;
  name: string | null; set: string | null; number: string | null; year: string | null;
  category: "tcg" | "sports" | "non_sports"; game: string | null; sport: string | null; player: string | null;
  manufacturer: string | null; parallel: string; serial: string | null; language: string | null; rarity: string;
  autograph: boolean; relic: boolean; is_slab: boolean; source_of_record: "cert" | "read";
};
export type ListerCertConflict = { field: "name" | "set" | "number" | "grade" | "tier" | "year"; read: string | null; cert: string | null };
/** Per-field provenance: which side of the reconciliation actually supplied each
 *  cert-checked field's value (`CertFieldProvenance`, `src/lister/card/identity.ts`). A
 *  field reads "cert" only when the grader's own record had a non-null value for it —
 *  `cert_verified` and `cert_bound` describe the lookup as a whole and must never stand
 *  in for checking this map before presenting one specific field as grader-confirmed. */
export type ListerCertFieldProvenance = Record<ListerCertConflict["field"], "cert" | "read">;
/** EXACTLY what `cert_json` holds server-side (`CertEnvelope`, Plan 1 `src/lister/card/identity.ts`).
 *  `session.cert` is this envelope, never a bare `ListerCertRecord`. Task 13 mirrored only
 *  `{ record, facts, conflicts, cert_verified }`; `cert_bound` and `field_source` are on the
 *  real server type too (`sessionView` parses `cert_json` straight into it, see
 *  `src/lister/pipeline.ts`) and Task 14's panel needs both — a record that cannot be bound
 *  to the requested certificate (ARS) or that verified but confirmed a given field with
 *  nothing (also ARS, for `grade`) must never be shown as that field's verification. */
export type ListerCertEnvelope = {
  record: ListerCertRecord | null; facts: ListerCardFacts; conflicts: ListerCertConflict[];
  /** The lookup returned a record (`status === "ok"`). Does NOT say the record is this
   *  certificate's — see `cert_bound` — nor that it filled in any field — see `field_source`. */
  cert_verified: boolean;
  /** The record could only be this certificate's (keyed lookup, or the page printed the
   *  cert number back). False for ARS, whose page never prints the cert, and for every
   *  unverified lookup. An unbound record corroborates the photographs; it verifies nothing. */
  cert_bound: boolean;
  field_source: ListerCertFieldProvenance;
};
export type ListerCardPrice = { suggested_cents: number | null; basis: "sold_in_grade" | "ebay_guidance" | "active_in_grade" | "sold_in_condition" | "active_in_condition" | "none"; explain: string; sold_n: number; reference_cents: number | null; reference_source: string | null; flags: string[] };
/** Buy / Trade's own facts about this item (send-to-lister design, task L2) — the subset the
 *  Review page shows beside the Lister's own research/price. Mirrors `SessionView.buy_trade`
 *  (src/lister/pipeline.ts) exactly. Null for every session not created by Buy / Trade.
 *  grader/grade/cert (review I2(b)) are Buy / Trade's own entered values — shown on the badge
 *  and checked against the session's reconciled card facts for a mismatch, never used to
 *  override them. `condition` (X1, send-to-lister cross-app review) is Buy / Trade's own raw
 *  condition label, shown on the badge and flagged by `buyTradeConditionFlag` when a sealed
 *  item isn't "Factory sealed". `price_cents` (X2) is Buy / Trade's own price rounded UP to the
 *  whole dollar — the session's starting price — not the cent-exact figure Buy / Trade sent. */
export type ListerBuyTrade = { deal_number: string; sku: string; unit_cost_cents: number; quantity: number; price_cents: number; barcode: string | null; grader: string | null; grade: string | null; cert: string | null; condition: string | null };
export type ListerOperatorPhoto = { file: string; side: "front" | "back" | "extra"; bytes: number; w: number; h: number; sha256: string; served_url: string };

// line 306 (pre-widening) — a card session has NO gtin; the column is nullable since migration 021
export type ListerSession = {
  id: string; gtin: string | null; gtin_raw: string | null; mode: ListerMode; scan_source: string; status: ListerStatus;
  step_log: ListerStep[]; existing: ListerExisting | null; research: ListerResearch | null; identity: ListerIdentity | null;
  refute: ListerRefute | null; verdict: ListerVerdict | null; proposal: ListerProposal | null; edits: ListerEdits | null;
  schema: ListerSchema | null; shopify_product_id: string | null; admin_url: string | null; error: string | null;
  created_at: string; updated_at: string;
  read: ListerCardRead | null; cert: ListerCertEnvelope | null; card_price: ListerCardPrice | null;
  photos: ListerOperatorPhoto[]; dup_check: "deferred" | "ok" | "failed" | "warned" | "title_searched";
  /** The operator's own stated condition for a RAW card (spec §4 decision 2). Null until they
   *  pick one, or on any graded/barcode session — never defaulted. */
  card_condition: ListerCardCondition | null;
  /** Where the autograph sits, stated by the operator (migration 025). Null until they pick
   *  one, on a card the read did not flag, or on a barcode session — never defaulted. */
  card_autograph_type: ListerAutographType | null;
  /** Possible duplicates a RAW card's title search found. Never the restock path's `existing`
   *  — a raw card has no unique key, so these are a warning, not a match. */
  dup_candidates: ListerExisting[];
  /** Buy / Trade's own facts about this item (task L2). Null for every session not created by
   *  POST /api/lister/sessions/buy-trade. */
  buy_trade: ListerBuyTrade | null;
};
export type ListerSessionSummary = { id: string; gtin: string | null; mode: ListerMode; status: ListerStatus; created_at: string; updated_at: string; error: string | null; title: string | null; admin_url: string | null; verdict_level: "green" | "yellow" | "red" | null };
export type ListerLiveProgress = { stage: string; note: string; at: string; started_at: string };
export type ListerSessionStatus = { id: string; status: ListerStatus; step_log: ListerStep[]; verdict: ListerVerdict | null; error: string | null; updated_at: string; progress: ListerLiveProgress | null };
export type ListerVocab = { shop: { name: string; currency: string; weight_unit: "POUNDS" | "OUNCES" | "KILOGRAMS" | "GRAMS"; primary_domain: string | null }; product_types: string[]; vendors: string[]; tags: string[]; collections: Array<{ id: string; title: string; handle: string; is_smart: boolean; rule_tags: string[]; products_count: number }>; publications: Array<{ id: string; name: string }>; location_id: string | null; custom_metafield_definitions: Array<{ namespace: string; key: string; name: string; type: string }>; fetched_at: string };
export type TaxonomyHit = { id: string; full_name: string; name: string; is_leaf: boolean };

export type Api = {
  login(password: string): Promise<{ ok: true }>;
  logout(): Promise<{ ok: true }>;
  listSavedSearches(): Promise<SavedSearch[]>;
  getSavedSearch(id: number): Promise<SavedSearch>;
  createSavedSearch(input: SavedSearchInput): Promise<SavedSearch>;
  updateSavedSearch(id: number, patch: Partial<SavedSearchInput>): Promise<SavedSearch>;
  deleteSavedSearch(id: number): Promise<{ ok: true }>;
  listListings(savedSearchId: SavedSearchSelector): Promise<LiveListing[]>;
  getListingComps(listingId: number): Promise<ListingComps>;
  getListingTcgPlayer(listingId: number): Promise<{ products: TcgProduct[] }>;
  getListingPriceCharting(listingId: number): Promise<{ product: PriceChartingProduct | null }>;
  getListingPsa(listingId: number): Promise<ListingPsa>;
  scanNow(savedSearchId: SavedSearchSelector): Promise<{ ok: true; fanout?: number }>;
  listScanRuns(savedSearchId: SavedSearchSelector, limit?: number): Promise<ScanRun[]>;
  getHotDeals(): Promise<HotDealsSnapshot | null>;
  getHotDealsStatus(): Promise<HotDealsStatus>;
  refreshHotDeals(): Promise<{ status: string; refreshed_at: string | null }>;
  priceCheck(title: string): Promise<PriceCheckResult>;
  createListerSession(input: { gtin: string; source: "camera" | "photo" | "manual" | "hid"; format?: string; quantity?: number }): Promise<{ id: string; gtin: string; existing: ListerExisting | null; dup_check: "ok" | "failed" }>;
  restockListerSession(id: string, delta: number, mode?: "add" | "replace", confirmReplace?: boolean): Promise<{ available: number | null; mode: "add" | "replace" }>;
  getListerExistingDetail(id: string): Promise<{ existing: ListerExisting; detail: ListerProductDetail; rows: ListerEnrichRow[] }>;
  enrichListerSession(id: string, body: { apply?: Record<string, boolean>; images?: "skip" | "append" }): Promise<{ applied: string[]; warnings: string[]; admin_url: string }>;
  startListerResearch(id: string, force?: boolean): Promise<{ status: ListerStatus }>;
  getListerSession(id: string): Promise<ListerSession>;
  getListerStatus(id: string): Promise<ListerSessionStatus>;
  saveListerEdits(id: string, edits: ListerEdits): Promise<{ ok: true; card_price?: ListerCardPrice; repriced?: boolean }>;
  remapListerSession(id: string, categoryId?: string): Promise<{ status: ListerStatus }>;
  postListerSession(id: string, confirmedAttention?: boolean): Promise<{ productId: string; handle: string; adminUrl: string; warnings: string[] }>;
  decodeBarcodeImage(imageBase64: string, mediaType: string): Promise<{ decoded: string | null; vision: string | null; agree: boolean }>;
  getListerSchema(categoryId: string): Promise<ListerSchema>;
  getListerVocab(refresh?: boolean): Promise<ListerVocab>;
  searchTaxonomy(q: string): Promise<TaxonomyHit[]>;
  listListerSessions(limit?: number): Promise<ListerSessionSummary[]>;
  // Always "deferred" / null: a graded card has no cert until the read has run (spec §11).
  createCardListerSession(input: { front_b64: string; back_b64: string; extras_b64?: string[] }): Promise<{ id: string; mode: "card"; dup_check: "deferred"; existing: null; photos: ListerOperatorPhoto[] }>;
  startListerRead(id: string): Promise<{ status: ListerStatus }>;
  /** Operator photographs for an existing session — the barcode/sealed flow's answer to
   *  research finding no images. Returns the refreshed session. */
  addListerSessionPhotos(id: string, photosB64: string[]): Promise<ListerSession>;
};

export type PriceCheckComp = {
  item_id: string;
  title: string;
  total: number;
  sold_date: string;
  url: string;
  days_ago: number | null;
};

export type PriceCheckResult = {
  query: string;
  detected_grade: string | null;
  median: number | null;
  low: number | null;
  high: number | null;
  count: number;
  count_30d: number;
  last_sale_at: string | null;
  comps: PriceCheckComp[];
  rejected: { junk: number; foreign: number; wrong_grade: number };
  note: string;
};

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const ct = res.headers.get("content-type") ?? "";
  const body = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export function makeApi(opts: { fetcher?: Fetcher } = {}): Api {
  const fetcher = opts.fetcher ?? globalThis.fetch.bind(globalThis);

  function get<T>(url: string): Promise<T> {
    return fetcher(url, { method: "GET", credentials: "include" }).then(jsonOrThrow<T>);
  }
  function send<T>(method: "POST" | "PUT" | "DELETE", url: string, body?: unknown): Promise<T> {
    return fetcher(url, {
      method,
      credentials: "include",
      ...(body !== undefined
        ? { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }
        : {}),
    }).then(jsonOrThrow<T>);
  }

  return {
    login: (password) => send("POST", "/api/login", { password }),
    logout: () => send("POST", "/api/logout"),
    listSavedSearches: () => get("/api/saved-searches"),
    getSavedSearch: (id) => get(`/api/saved-searches/${id}`),
    createSavedSearch: (input) => send("POST", "/api/saved-searches", input),
    updateSavedSearch: (id, patch) => send("PUT", `/api/saved-searches/${id}`, patch),
    deleteSavedSearch: (id) => send("DELETE", `/api/saved-searches/${id}`),
    listListings: (id) => get(`/api/listings?savedSearchId=${id}`),
    getListingComps: (id) => get(`/api/listings/${id}/comps`),
    getListingTcgPlayer: (id) => get(`/api/listings/${id}/tcgplayer`),
    getListingPriceCharting: (id) => get(`/api/listings/${id}/pricecharting`),
    getListingPsa: (id) => get(`/api/listings/${id}/psa`),
    scanNow: (id) => send("POST", `/api/scan/now/${id}`),
    listScanRuns: (id, limit = 25) => get(`/api/scan/runs?savedSearchId=${id}&limit=${limit}`),
    getHotDeals: async () => {
      try {
        return await get<HotDealsSnapshot>("/api/hot-deals");
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    getHotDealsStatus: () => get("/api/hot-deals/status"),
    refreshHotDeals: () => send("POST", "/api/hot-deals/refresh"),
    priceCheck: (title) => send("POST", "/api/price-check", { title }),
    createListerSession: (input) =>
      send("POST", "/api/lister/sessions", {
        gtin: input.gtin,
        source: input.source,
        ...(input.format !== undefined ? { format: input.format } : {}),
        ...(input.quantity !== undefined ? { quantity: input.quantity } : {}),
      }),
    // `delta` is always sent from the browser — the modal's input can never be blank — so the
    // server-side scan-quantity fallback exists only for a reload or an older bundle.
    restockListerSession: (id, delta, mode, confirmReplace) =>
      send("POST", `/api/lister/sessions/${id}/restock`, { delta, ...(mode ? { mode } : {}), ...(confirmReplace ? { confirm_replace: true } : {}) }),
    getListerExistingDetail: (id) => get(`/api/lister/sessions/${id}/existing`),
    enrichListerSession: (id, body) => send("POST", `/api/lister/sessions/${id}/enrich`, body),
    startListerResearch: (id, force) => send("POST", `/api/lister/sessions/${id}/research${force ? "?force=1" : ""}`),
    getListerSession: (id) => get(`/api/lister/sessions/${id}`),
    getListerStatus: (id) => get(`/api/lister/sessions/${id}?view=status`),
    saveListerEdits: (id, edits) => send("PUT", `/api/lister/sessions/${id}/edits`, edits),
    remapListerSession: (id, categoryId) => send("POST", `/api/lister/sessions/${id}/remap`, categoryId ? { category_id: categoryId } : {}),
    postListerSession: (id, confirmedAttention) => send("POST", `/api/lister/sessions/${id}/post`, { confirmed_attention: Boolean(confirmedAttention) }),
    decodeBarcodeImage: (imageBase64, mediaType) => send("POST", "/api/lister/decode", { image: imageBase64, media_type: mediaType }),
    getListerSchema: (categoryId) => get(`/api/lister/schema?category=${encodeURIComponent(categoryId)}`),
    getListerVocab: (refresh) => get(`/api/lister/vocab${refresh ? "?refresh=1" : ""}`),
    searchTaxonomy: (q) => get(`/api/lister/taxonomy?q=${encodeURIComponent(q)}`),
    listListerSessions: (limit = 50) => get(`/api/lister/sessions?limit=${limit}`),
    createCardListerSession: (input) => send("POST", "/api/lister/sessions/card", { front_b64: input.front_b64, back_b64: input.back_b64, extras_b64: input.extras_b64 ?? [] }),
    startListerRead: (id) => send("POST", `/api/lister/sessions/${id}/read`),
    addListerSessionPhotos: (id, photosB64) => send("POST", `/api/lister/sessions/${id}/photos`, { photos_b64: photosB64 }),
  };
}

export const api: Api = makeApi();

// GET /api/lister/sessions/:id/label (send-to-lister, task L4): not an Api method — it answers
// text/html, not JSON, and printDocument (ui/src/lib/print.ts) needs the bare path to open the
// print window and fetch it directly, rather than going through get()/jsonOrThrow() above. This
// is the one place the path is written down, so a caller never spells it out itself.
export const listerSessionLabelPath = (id: string): string => `/api/lister/sessions/${id}/label`;
